import { useState, useEffect } from 'react';
import {
  List, ListItem, ListItemText, Typography, Box, TextField, InputAdornment,
  Button, Modal, IconButton, Divider, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LoadingAnimation from './LoadingAnimation';
import FloatingActionButton from './FloatingActionButton';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { fetchTemplates, addTemplate, updateTemplate, deleteTemplate } from '../api/github';

const AddTemplateModal = ({ open, onClose, onAddTemplate }) => {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || !text.trim()) {
      alert('Илтимос, ном ва текстни киритинг');
      return null;
    }

    setIsSubmitting(true);
    try {
      await onAddTemplate({ title, text });
      setTitle('');
      setText('');
      onClose(true);
    } catch (error) {
      console.error('Шаблон қўшишда хатолик:', error);
      alert(error.message || 'Шаблон қўшишда хатолик');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !isSubmitting && onClose(false)}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        boxShadow: 24,
        width: '90%',
        maxWidth: 500,
        p: 3,
      }}>
        <Typography variant="h6" gutterBottom>
          Шаблон қўшиш
        </Typography>
        <TextField
          label="Шаблон номи"
          fullWidth
          margin="normal"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={isSubmitting}
        />
        <TextField
          label="Шаблон тексти"
          fullWidth
          margin="normal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          rows={4}
          required
          disabled={isSubmitting}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Сақлаш'}
          </Button>
          <Button onClick={() => !isSubmitting && onClose(false)} disabled={isSubmitting}>
            Бекор қилиш
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const EditTemplateModal = ({ open, onClose, onEditTemplate, template }) => {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (template) {
      setTitle(template.title || '');
      setText(template.text || '');
    }
  }, [template]);

  const handleSubmit = async () => {
    if (!title.trim() || !text.trim()) {
      alert('Илтимос, ном ва текстни киритинг');
      return null;
    }

    setIsSubmitting(true);
    try {
      await onEditTemplate(template.id, { title, text });
      onClose(true);
    } catch (error) {
      console.error('Шаблон таҳрирлашда хатолик:', error);
      alert(error.message || 'Шаблон таҳрирлашда хатолик');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !isSubmitting && onClose(false)}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        boxShadow: 24,
        width: '90%',
        maxWidth: 500,
        p: 3,
      }}>
        <Typography variant="h6" gutterBottom>
          Шаблон таҳрирлаш
        </Typography>
        <TextField
          label="Шаблон номи"
          fullWidth
          margin="normal"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={isSubmitting}
        />
        <TextField
          label="Шаблон тексти"
          fullWidth
          margin="normal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          rows={4}
          required
          disabled={isSubmitting}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Сақлаш'}
          </Button>
          <Button onClick={() => !isSubmitting && onClose(false)} disabled={isSubmitting}>
            Бекор қилиш
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default function TemplatesList({ isMobile, refreshTrigger }) {
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      try {
        const data = await fetchTemplates();
        setTemplates(data || []);
      } catch (error) {
        console.error('Шаблонлар юкланишда хатолик:', error);
        alert(error.message || 'Шаблонлар юкланишда хатолик');
      } finally {
        setLoading(false);
      }
    };
    loadTemplates();
  }, [refreshTrigger]);

  const handleAddTemplate = async (templateData) => {
    try {
      const newTemplate = await addTemplate(templateData);
      setTemplates(prev => [...prev, newTemplate]);
    } catch (error) {
      throw error;
    }
  };

  const handleEditTemplate = async (id, updatedData) => {
    try {
      await updateTemplate(id, updatedData);
      setTemplates(prev =>
        prev.map(template =>
          template.id === id ? { ...template, ...updatedData } : template
        )
      );
    } catch (error) {
      throw error;
    }
  };

  const handleCopyTemplate = (text) => {
    // Функция для фолбэка через textarea
    const fallbackCopyText = (text) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          alert('Шаблон тексти нусха олинди!');
        } else {
          throw new Error('Копирование не удалось');
        }
      } catch (err) {
        console.error('Фолбэк копирование не удалось:', err);
        alert('Тексти нусха олишда хатолик юз берди. Илтимос, текстни қўлда нусха олинг.');
      } finally {
        document.body.removeChild(textarea);
      }
    };

    // Основной метод с использованием Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          alert('Шаблон тексти нусха олинди!');
        })
        .catch(err => {
          console.error('Ошибка копирования через Clipboard API:', err);
          fallbackCopyText(text);
        });
    } else {
      console.warn('Clipboard API не поддерживается, используется фолбэк');
      fallbackCopyText(text);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await deleteTemplate(templateToDelete);
      setTemplates(prev => prev.filter(t => t.id !== templateToDelete));
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Шаблон ўчиришда хатолик:', error);
      alert(error.message || 'Шаблон ўчиришда хатолик');
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FloatingActionButton
          onClick={() => setAddModalOpen(true)}
          tooltip="Шаблон қўшиш"
        />
      </Box>
      {loading ? (
        <LoadingAnimation loading={loading} />
      ) : templates.length === 0 ? (
        <Typography variant="body1" sx={{ mt: 2 }}>
          Шаблонлар мавжуд эмас
        </Typography>
      ) : (
        <Box>
          <TextField
            placeholder="Ном бўйича қидириш..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <List>
            {filteredTemplates.map(template => (
              <ListItem key={template.id} divider>
                <ListItemText
                  primary={template.title}
                  secondary={template.text.length > 100 ? `${template.text.substring(0, 100)}...` : template.text}
                />
                <IconButton
                  onClick={() => {
                    setSelectedTemplate(template);
                    setEditModalOpen(true);
                  }}
                  title="Шаблонни таҳрирлаш"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleCopyTemplate(template.text)}
                  title="Тексти нусха олиш"
                >
                  <ContentCopyIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setTemplateToDelete(template.id);
                    setDeleteDialogOpen(true);
                  }}
                  title="Шаблонни ўчириш"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      <AddTemplateModal
        open={addModalOpen}
        onClose={(shouldRefresh) => {
          setAddModalOpen(false);
          if (shouldRefresh) {
            const loadTemplates = async () => {
              setLoading(true);
              try {
                const data = await fetchTemplates();
                setTemplates(data || []);
              } catch (error) {
                console.error('Шаблонлар юкланишда хатолик:', error);
                alert(error.message || 'Шаблонлар юкланишда хатолик');
              } finally {
                setLoading(false);
              }
            };
            loadTemplates();
          }
        }}
        onAddTemplate={handleAddTemplate}
      />
      <EditTemplateModal
        open={editModalOpen}
        onClose={(shouldRefresh) => {
          setEditModalOpen(false);
          setSelectedTemplate(null);
          if (shouldRefresh) {
            const loadTemplates = async () => {
              setLoading(true);
              try {
                const data = await fetchTemplates();
                setTemplates(data || []);
              } catch (error) {
                console.error('Шаблонлар юкланишда хатолик:', error);
                alert(error.message || 'Шаблонлар юкланишда хатолик');
              } finally {
                setLoading(false);
              }
            };
            loadTemplates();
          }
        }}
        onEditTemplate={handleEditTemplate}
        template={selectedTemplate}
      />
      <ConfirmDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteTemplate}
        itemName="Шаблон"
        title="Шаблонни ўчириш"
        message="Хақиқатан хам бу шаблонни ўчирмоқчимисиз?"
        confirmText="Ҳа"
        cancelText="Йўқ"
      />
    </Box>
  );
}